import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Clock, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import axiosInstance from '@/utils/axios';

const PaymentResultPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);

  const status = searchParams.get('collection_status') || searchParams.get('status');
  const paymentId = searchParams.get('payment_id');
  const externalReference = searchParams.get('external_reference');

  useEffect(() => {
    if (paymentId) {
      fetchPaymentInfo();
    } else {
      setLoading(false);
    }
  }, [paymentId]);

  const fetchPaymentInfo = async () => {
    try {
      const { data } = await axiosInstance.get(`/payments/history`);
      const payment = data.payments.find(p => p.payment_id === paymentId);
      setPaymentInfo(payment);
    } catch (error) {
      console.error('Error fetching payment info:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = () => {
    if (status === 'approved') {
      return {
        icon: <CheckCircle className="w-16 h-16 text-green-500" />,
        title: '¡Pago Exitoso!',
        description: 'Tu pago ha sido procesado correctamente',
        color: 'green',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      };
    } else if (status === 'pending' || status === 'in_process') {
      return {
        icon: <Clock className="w-16 h-16 text-amber-500" />,
        title: 'Pago Pendiente',
        description: 'Tu pago está siendo procesado',
        color: 'amber',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200'
      };
    } else {
      return {
        icon: <AlertCircle className="w-16 h-16 text-red-500" />,
        title: 'Pago Rechazado',
        description: 'Hubo un problema con tu pago',
        color: 'red',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      };
    }
  };

  const statusConfig = getStatusConfig();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        <Card className={`border-2 ${statusConfig.borderColor}`}>
          <CardHeader className={`${statusConfig.bgColor} text-center pb-8`}>
            <div className="flex justify-center mb-4">
              {statusConfig.icon}
            </div>
            <CardTitle className="text-2xl font-heading font-bold">
              {statusConfig.title}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {statusConfig.description}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            {paymentInfo && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Plan:</span>
                  <span className="font-semibold text-slate-900">{paymentInfo.plan_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Monto:</span>
                  <span className="font-semibold text-slate-900">
                    ${paymentInfo.amount?.toLocaleString('es-CO')} COP
                  </span>
                </div>
                {paymentId && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">ID de Pago:</span>
                    <span className="font-mono text-xs text-slate-900">{paymentId.substring(0, 16)}...</span>
                  </div>
                )}
              </div>
            )}

            {status === 'approved' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-800">
                  {paymentInfo?.payment_type === 'publish'
                    ? 'Tu propiedad ha sido activada y ahora está visible para compradores.'
                    : 'Tu propiedad ahora aparece destacada en los resultados de búsqueda.'}
                </p>
              </div>
            )}

            {status === 'pending' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800">
                  Tu pago está siendo procesado. Recibirás una notificación cuando se complete.
                </p>
              </div>
            )}

            {status !== 'approved' && status !== 'pending' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-800">
                  El pago no pudo ser procesado. Por favor, intenta nuevamente o usa otro método de pago.
                </p>
              </div>
            )}

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/perfil')}
                className="w-full bg-primary hover:bg-primary-hover"
                data-testid="go-to-profile"
              >
                Ver Mis Propiedades
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full"
                data-testid="go-home"
              >
                <Home className="w-4 h-4 mr-2" />
                Volver al Inicio
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentResultPage;